import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatDateISO } from '../../utils/dateUtils';
import {
  ArrowLeftIcon,
  PencilIcon,
  KeyIcon,
  DocumentIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../Common/LoadingSpinner';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    role: 'student',
    isActive: true,
    isApproved: true,
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    instructorProfile: {
      qualification: '',
      experience: 0,
      specialization: [''],
      bio: '',
      linkedIn: '',
      portfolio: ''
    }
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`/api/users/${userId}`);
      const userData = response.data;
      setUser(userData);

      // Populate form with user data
      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        dateOfBirth: userData.dateOfBirth ? formatDateISO(userData.dateOfBirth) : '',
        role: userData.role || 'student',
        isActive: userData.isActive !== undefined ? userData.isActive : true,
        isApproved: userData.isApproved !== undefined ? userData.isApproved : true,
        address: {
          street: userData.address?.street || '',
          city: userData.address?.city || '',
          state: userData.address?.state || '',
          zipCode: userData.address?.zipCode || '',
          country: userData.address?.country || ''
        },
        instructorProfile: userData.instructorProfile ? {
          qualification: userData.instructorProfile.qualification || '',
          experience: userData.instructorProfile.experience || 0,
          specialization: userData.instructorProfile.specialization?.length
            ? userData.instructorProfile.specialization
            : [''],
          bio: userData.instructorProfile.bio || '',
          linkedIn: userData.instructorProfile.linkedIn || '',
          portfolio: userData.instructorProfile.portfolio || ''
        } : {
          qualification: '',
          experience: 0,
          specialization: [''],
          bio: '',
          linkedIn: '',
          portfolio: ''
        }
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to load user details');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      if (parent === 'address') {
        setFormData(prev => ({
          ...prev,
          address: { ...prev.address, [child]: value }
        }));
      } else if (parent === 'instructorProfile') {
        setFormData(prev => ({
          ...prev,
          instructorProfile: { ...prev.instructorProfile, [child]: type === 'number' ? Number(value) : value }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSpecializationChange = (index, value) => {
    const newSpecialization = [...formData.instructorProfile.specialization];
    newSpecialization[index] = value;
    setFormData(prev => ({
      ...prev,
      instructorProfile: {
        ...prev.instructorProfile,
        specialization: newSpecialization
      }
    }));
  };

  const addSpecialization = () => {
    setFormData(prev => ({
      ...prev,
      instructorProfile: {
        ...prev.instructorProfile,
        specialization: [...prev.instructorProfile.specialization, '']
      }
    }));
  };

  const removeSpecialization = (index) => {
    const newSpecialization = formData.instructorProfile.specialization.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      instructorProfile: {
        ...prev.instructorProfile,
        specialization: newSpecialization.length ? newSpecialization : ['']
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = { ...formData };

      // Clean up specializations
      if (payload.instructorProfile.specialization) {
        payload.instructorProfile.specialization = payload.instructorProfile.specialization.filter(spec => spec.trim() !== '');
      }

      await axios.put(`/api/admin/users/${userId}`, payload);
      toast.success('User updated successfully!');

      // Refresh user data
      fetchUser();
    } catch (error) {
      console.error('Update error:', error);
      const message = error.response?.data?.message || 'Failed to update user';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setSaving(true);
      await axios.put(`/api/admin/users/${userId}/password`, {
        password: passwordData.newPassword
      });
      toast.success('Password updated successfully!');

      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Password update error:', error);
      toast.error('Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateUser = async () => {
    if (!user || !user.isActive) return;

    try {
      await axios.put(`/api/users/${userId}/deactivate`);
      toast.success('User deactivated successfully');
      fetchUser();
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast.error('Failed to deactivate user');
    }
  };

  const handleActivateUser = async () => {
    if (!user || user.isActive) return;

    try {
      await axios.put(`/api/users/${userId}/approve`);
      toast.success('User activated successfully');
      fetchUser();
    } catch (error) {
      console.error('Error activating user:', error);
      toast.error('Failed to activate user');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'instructor': return 'bg-blue-100 text-blue-800';
      case 'student': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (isActive, isApproved) => {
    if (!isActive) return 'bg-red-100 text-red-800';
    if (!isApproved) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">User not found</h2>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to User Management
        </button>

        <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
        <p className="mt-2 text-gray-600">
          View and manage user account information
        </p>
      </div>

      {/* User Overview Card */}
      <div className="card mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-6">
            <div className="h-16 w-16 bg-gray-300 rounded-full flex items-center justify-center">
              <span className="text-xl font-medium text-gray-600">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </span>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-gray-600">{user.email}</p>

              <div className="flex items-center space-x-4 mt-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.isActive, user.isApproved)}`}>
                  {!user.isActive ? 'Inactive' : !user.isApproved ? 'Pending Approval' : 'Active'}
                </span>
              </div>
            </div>
          </div>

          <div className="text-right text-sm text-gray-500">
            <p>Joined: {new Date(user.enrollmentDate).toLocaleDateString()}</p>
            {user.lastLogin && (
              <p>Last Login: {new Date(user.lastLogin).toLocaleDateString()}</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Profile Information
            </button>
            {user.role == 'instructor' && (
            <button
              onClick={() => setActiveTab('instructor')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'instructor'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Instructor Profile
            </button>
            )}
            <button
              onClick={() => setActiveTab('password')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'password'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Change Password
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="student">Student</option>
                  <option value="instructor">Instructor</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Active
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Account is active</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Approved
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isApproved"
                    checked={formData.isApproved}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Account is approved</span>
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleInputChange}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleInputChange}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    name="address.zipCode"
                    value={formData.address.zipCode}
                    onChange={handleInputChange}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    name="address.country"
                    value={formData.address.country}
                    onChange={handleInputChange}
                    className="input"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <div>
                {user.isActive ? (
                  <button
                    type="button"
                    onClick={() => handleDeactivateUser()}
                    className="btn btn-danger flex items-center"
                  >
                    <XCircleIcon className="h-5 w-5 mr-2" />
                    Deactivate User
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleActivateUser()}
                    className="btn btn-success flex items-center"
                  >
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Activate User
                  </button>
                )}
              </div>

              <button
                type="submit"
                disabled={saving}
                className="btn btn-primary disabled:opacity-50 flex items-center"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                {saving ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'instructor' && (
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Highest Qualification
                </label>
                <input
                  type="text"
                  name="instructorProfile.qualification"
                  value={formData.instructorProfile.qualification}
                  onChange={handleInputChange}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Years of Experience
                </label>
                <input
                  type="number"
                  name="instructorProfile.experience"
                  value={formData.instructorProfile.experience}
                  onChange={handleInputChange}
                  min="0"
                  className="input"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn Profile
                </label>
                <input
                  type="url"
                  name="instructorProfile.linkedIn"
                  value={formData.instructorProfile.linkedIn}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portfolio URL
                </label>
                <input
                  type="url"
                  name="instructorProfile.portfolio"
                  value={formData.instructorProfile.portfolio}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="https://portfolio.com"
                />
              </div>
            </div>

            {/* Specialization */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Areas of Specialization</h3>
              <div className="space-y-3">
                {formData.instructorProfile.specialization?.map((spec, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={spec}
                      onChange={(e) => handleSpecializationChange(index, e.target.value)}
                      className="input flex-1"
                      placeholder="e.g., Web Development, Data Science"
                    />
                    {formData.instructorProfile.specialization?.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSpecialization(index)}
                        className="btn btn-danger px-3 py-2"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addSpecialization}
                  className="btn btn-secondary"
                >
                  + Add Specialization
                </button>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Professional Bio
              </label>
              <textarea
                name="instructorProfile.bio"
                value={formData.instructorProfile.bio}
                onChange={handleInputChange}
                rows={5}
                className="input"
                placeholder="Brief description of teaching experience and expertise..."
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="btn btn-primary disabled:opacity-50 flex items-center"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                {saving ? 'Updating...' : 'Update Instructor Profile'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'password' && (
        <div className="card">
          <div className="flex items-center mb-6">
            <KeyIcon className="h-6 w-6 text-gray-400 mr-3" />
            <h2 className="text-lg font-medium text-gray-900">Change User Password</h2>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password *
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="input"
                  required
                  minLength={6}
                />
                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="input"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="btn btn-primary disabled:opacity-50 flex items-center"
              >
                <KeyIcon className="h-5 w-5 mr-2" />
                {saving ? 'Updating Password...' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
